"use client";

import { frontendImage } from "@/lib/helpers";
import Image from "next/image";
import useUtility from "@/app/_hooks/useUtility";
import { useSection } from "./_hooks/useSection";
import CountrySkeleton from "./_skeletons/CountrySkeleton";
import countryFlags from "./countryFlags";
import { motion } from "framer-motion";

export const Country = () => {
  const { trans } = useUtility();
  const { content, elements, loading } = useSection("country");

  if (loading) return <CountrySkeleton />;

  const flagCountries = Object.keys(countryFlags);
  // Duplicate for seamless infinite scroll
  const duplicatedFlags = [...flagCountries, ...flagCountries];

  return (
    <section
      className="country-section pt-70 bg-img"
      style={{
        position: "relative",
        backgroundImage: `url(${frontendImage(
          content?.background_image,
          "country"
        )})`
      }}
    >
      <div className="country-section-overlay" />

      <div className="container" style={{ paddingTop: "40px" }}>
        <div className="row justify-content-center">
          <div className="col-xl-10">
            <div
              className="section-heading text-center"
              style={{ marginBottom: "20px" }}
            >
              <motion.span
                className="section-heading__subtitle"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.1 }}
              >
                {trans(content?.subtitle)}
                <Image
                  src={frontendImage(content?.title_icon, "country")}
                  alt="Subtitle Shape"
                  width={20}
                  height={20}
                />
              </motion.span>
              <motion.h2
                className="section-heading__title country-gradient-text mb-0"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.2 }}
              >
                From Ghana to Kenya, we've <br />
                got you covered!
              </motion.h2>
            </div>
          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-xl-12">
            <div className="country-carousel-wrapper">
              {/* First Row - Scroll Left */}
              <div className="country-carousel-row">
                <div className="country-fade-left" />
                <div className="country-fade-right" />
                <div className="country-carousel-track scroll-left">
                  {duplicatedFlags.map((country, index) => (
                    <div
                      key={`row1-${country}-${index}`}
                      className="country-flag-item"
                    >
                      <div className="flag-img-wrapper">
                        <Image
                          src={countryFlags[country]}
                          alt={`Flag of ${country}`}
                          width={80}
                          height={60}
                        />
                      </div>
                      <span className="country-name">{country}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Second Row - Scroll Right */}
              <div className="country-carousel-row">
                <div className="country-fade-left" />
                <div className="country-fade-right" />
                <div className="country-carousel-track scroll-right">
                  {duplicatedFlags.map((country, index) => (
                    <div
                      key={`row2-${country}-${index}`}
                      className="country-flag-item"
                    >
                      <div className="flag-img-wrapper">
                        <Image
                          src={countryFlags[country]}
                          alt={`Flag of ${country}`}
                          width={80}
                          height={60}
                        />
                      </div>
                      <span className="country-name">{country}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx global>{`
        .country-section-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(255, 251, 238, 0.5);
          z-index: 1;
          pointer-events: none;
        }

        .country-carousel-wrapper {
          position: relative;
          z-index: 2;
          display: flex;
          flex-direction: column;
          gap: 20px;
          margin-top: 20px;
          overflow: hidden;
        }

        .country-carousel-row {
          position: relative;
          overflow: hidden;
          width: 100%;
        }

        .country-carousel-track {
          display: flex;
          width: max-content;
        }

        .country-carousel-track.scroll-left {
          animation: scrollLeft 30s linear infinite;
        }

        .country-carousel-track.scroll-right {
          animation: scrollRight 30s linear infinite;
        }

        .country-carousel-row:hover .country-carousel-track {
          animation-play-state: paused;
        }

        @keyframes scrollLeft {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }

        @keyframes scrollRight {
          0% {
            transform: translateX(-50%);
          }
          100% {
            transform: translateX(0);
          }
        }

        .country-flag-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 10px 15px;
          flex-shrink: 0;
        }

        .flag-img-wrapper {
          width: 80px;
          height: 60px;
          border-radius: 8px;
          overflow: hidden;
          transition: transform 0.3s ease;
          cursor: pointer;
        }

        .flag-img-wrapper:hover {
          transform: scale(1.05);
        }

        .flag-img-wrapper img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 8px;
        }

        .country-name {
          text-align: center;
          margin-top: 8px;
          font-size: 14px;
          font-weight: 500;
          white-space: nowrap;
        }

        .country-fade-left,
        .country-fade-right {
          position: absolute;
          top: 0;
          width: 40px;
          height: 100%;
          z-index: 10;
          pointer-events: none;
        }

        .country-fade-left {
          left: 0;
          background: linear-gradient(
            to right,
            #fffbee 0%,
            rgba(255, 251, 238, 0) 100%
          );
        }

        .country-fade-right {
          right: 0;
          background: linear-gradient(
            to left,
            #fffbee 0%,
            rgba(255, 251, 238, 0) 100%
          );
        }

        /* Tablet */
        @media (max-width: 768px) {
          .country-flag-item {
            padding: 8px 12px;
          }

          .flag-img-wrapper {
            width: 75px;
            height: 60px;
          }

          .country-name {
            font-size: 12px;
          }

          .country-carousel-track.scroll-left,
          .country-carousel-track.scroll-right {
            animation-duration: 25s;
          }
        }

        /* Mobile */
        @media (max-width: 480px) {
          .country-carousel-wrapper {
            gap: 15px;
          }

          .country-flag-item {
            padding: 6px 10px;
          }

          .flag-img-wrapper {
            width: 60px;
            height: 45px;
          }

          .country-name {
            font-size: 11px;
            max-width: 65px;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .country-carousel-track.scroll-left,
          .country-carousel-track.scroll-right {
            animation-duration: 20s;
          }

          .country-fade-left,
          .country-fade-right {
            width: 25px;
          }
        }
      `}</style>
    </section>
  );
};
